import React, { useState, useRef, useEffect } from "react";
import { FaPlay, FaPause } from "react-icons/fa";

const MindfulnessAudioPlayer = ({ audio }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(new Audio());
  const animationRef = useRef();
  const prevAudioRef = useRef(null);

  // Format time in MM:SS
  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Handle audio changes and auto-play
  useEffect(() => {
    if (!audio?.audio) return;

    // Check if audio has changed
    if (audio.audio !== prevAudioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = audio.audio;
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          animationRef.current = requestAnimationFrame(updateTime);
          setCurrentTime(0);
          prevAudioRef.current = audio.audio;
        })
        .catch((error) => console.log("Playback failed:", error));
    }
  }, [audio]);

  // Update current time during playback
  const updateTime = () => {
    setCurrentTime(audioRef.current.currentTime);
    if (audioRef.current.ended) {
      setIsPlaying(false);
      cancelAnimationFrame(animationRef.current);
    } else {
      animationRef.current = requestAnimationFrame(updateTime);
    }
  };

  // Toggle play/pause
  const togglePlay = () => {
    if (!audio) return;
    if (isPlaying) {
      audioRef.current.pause();
      cancelAnimationFrame(animationRef.current);
    } else {
      audioRef.current.play();
      animationRef.current = requestAnimationFrame(updateTime);
    }
    setIsPlaying(!isPlaying);
  };

  // Handle slider change
  const handleSeek = (e) => {
    const newTime = Number(e.target.value);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Set duration when metadata is loaded
  useEffect(() => {
    const audio = audioRef.current;
    const setDurationHandler = () => setDuration(audio.duration);

    audio.addEventListener("loadedmetadata", setDurationHandler);
    return () =>
      audio.removeEventListener("loadedmetadata", setDurationHandler);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioRef.current.pause();
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <div className="fixed bottom-0 w-full bg-white shadow-lg">
      <div className="max-w-4xl mx-auto py-4 px-4">
        <div className="flex items-center gap-4">
          <button
            onClick={togglePlay}
            className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isPlaying ? "bg-gray-100" : "bg-indigo-600"
            }`}
          >
            {isPlaying ? (
              <FaPause className="w-6 h-6 text-indigo-600" />
            ) : (
              <FaPlay className="w-6 h-6 text-white" />
            )}
          </button>

          <div className="flex-1">
            <h4 className="font-bold text-sm truncate">
              {audio?.title || "Select a mindfulness exercise"}
            </h4>

            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />

            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MindfulnessAudioPlayer;
